import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { API_URL } from '../config';

/**
 * Shared data layer for the Meta and Google performance screens.
 *
 * Both screens fetch the same four resources (dashboard, inactive accounts,
 * alerts, health overview) and differ only in the platform they filter on, the
 * label used in toasts, and how far back they are willing to fall back when the
 * selected timeframe returns no spend.
 *
 * `scoreClient` is injected rather than imported: the status filter needs a
 * client score, and scoring is genuinely platform-specific.
 */
const usePerformanceData = ({
  platform,
  label,
  inactiveKey,
  fallbackRanges,
  scoreClient,
  addToast,
}) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [inactiveData, setInactiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');
  const [filterStatus, setFilterStatus] = useState('all');
  const [timeframe, setTimeframe] = useState('last_7_days');
  const [accountSearch, setAccountSearch] = useState('');
  const [aiAnalysisClient, setAiAnalysisClient] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsUnreadCount, setAlertsUnreadCount] = useState(0);

  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState(null);

  const fetchAiInsights = useCallback(async (selectedTimeframe) => {
    setAiInsightsLoading(true);
    setAiInsightsError(null);
    try {
      const tf = selectedTimeframe || timeframe;
      const response = await fetch(`${API_URL}/ai/portfolio-insights?platform=${platform}&date_range=${tf}`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('AI analysis failed');
      const result = await response.json();
      setAiInsights(result);
    } catch (error) {
      toast.error('Failed to load AI insights');
      setAiInsightsError(error.message);
    } finally {
      setAiInsightsLoading(false);
    }
  }, [timeframe, platform]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/alerts?platform=${platform}`);
      if (!response.ok) throw new Error('Failed to fetch alerts');
      const result = await response.json();
      // Alerts are already filtered by platform on the backend
      setAlerts(result.alerts || []);
      setAlertsUnreadCount((result.alerts || []).filter(a => !a.is_read).length);
    } catch (error) {
      toast.error(`Failed to load ${label} alerts`);
    }
  }, [platform, label]);

  // Pass current timeframe for accurate spend context
  const fetchHealthData = useCallback(async (selectedTimeframe) => {
    try {
      const tf = selectedTimeframe || timeframe;
      const response = await fetch(`${API_URL}/health-overview?date_range=${tf}`);
      if (!response.ok) throw new Error('Failed to fetch health');
      const result = await response.json();
      const healthy = (result.healthy || []).filter(a => a.platform === platform);
      const warning = (result.warning || []).filter(a => a.platform === platform);
      const critical = (result.critical || []).filter(a => a.platform === platform);
      setHealthData({
        healthy,
        warning,
        critical,
        total: healthy.length + warning.length + critical.length
      });
    } catch (error) {
      toast.error(`Failed to load ${label} health data`);
    }
  }, [timeframe, platform, label]);

  const fetchAllData = useCallback(async (selectedTimeframe) => {
    setLoading(true);
    try {
      const tf = selectedTimeframe || timeframe;
      const [dashboardRes, inactiveRes] = await Promise.all([
        fetch(`${API_URL}/dashboard/fast?date_range=${tf}`),
        fetch(`${API_URL}/inactive-accounts/fast?platform=${platform}&date_range=yesterday`)
      ]);

      let dashboard = await dashboardRes.json();

      // If no cached data, fall back to regular endpoint
      if (dashboard.status === 'no_data') {
        const fallbackRes = await fetch(`${API_URL}/dashboard`);
        dashboard = await fallbackRes.json();
      }

      // Smart fallback: only attempt wider ranges on initial load, not on an
      // explicit user timeframe change.
      const withSpend = (dashboard?.accounts || []).filter(a => a.platform === platform && (a.metrics?.spend || 0) > 0);
      if (withSpend.length === 0 && dashboard.status !== 'no_data' && !selectedTimeframe) {
        for (const fallbackRange of fallbackRanges) {
          if (fallbackRange === tf) continue;
          const fbRes = await fetch(`${API_URL}/dashboard/fast?date_range=${fallbackRange}`);
          const fbData = await fbRes.json();
          const fbAccounts = (fbData?.accounts || []).filter(a => a.platform === platform && (a.metrics?.spend || 0) > 0);
          if (fbAccounts.length > 0) {
            dashboard = { ...fbData, _fallback_range: fallbackRange };
            break;
          }
        }
      }

      const inactive = await inactiveRes.json();
      // Normalize field names so InactiveAccountCard's spend_2_days_ago renders correctly
      if (inactive[inactiveKey]) {
        inactive[inactiveKey] = inactive[inactiveKey].map(a => ({
          ...a,
          spend_2_days_ago: a.spend_day_before,
        }));
      }

      setDashboardData(dashboard);
      setInactiveData(inactive);
    } catch (error) {
      toast.error(`Failed to load ${label} accounts`);
    }
    setLoading(false);
  }, [timeframe, platform, label, inactiveKey, fallbackRanges]);

  useEffect(() => {
    fetchAllData();
    fetchAlerts();
    fetchHealthData();
  }, [fetchAllData, fetchAlerts, fetchHealthData]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    fetchAllData(newTimeframe);
    fetchHealthData(newTimeframe);
  };

  const markAlertAsRead = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/alerts/${alertId}/read`, { method: 'PUT' });
      if (!res.ok) throw new Error('Failed to mark as read');
      await fetchAlerts();
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await fetch(`${API_URL}/alerts/${alertId}/resolve`, { method: 'PUT' });
      addToast('Alert resolved', 'success');
      await fetchAlerts();
    } catch (error) {
      addToast('Failed to resolve', 'error');
    }
  };

  // Sorted by spend descending
  const platformClients = useMemo(() => {
    const accounts = dashboardData?.accounts?.filter(a => a.platform === platform) || [];
    return accounts
      .sort((a, b) => (b.metrics?.spend || 0) - (a.metrics?.spend || 0));
  }, [dashboardData, platform]);

  const filteredClients = useMemo(() => {
    let list = platformClients;
    if (accountSearch.trim()) {
      const q = accountSearch.toLowerCase();
      list = list.filter(c => (c.name || '').toLowerCase().includes(q));
    }
    if (filterStatus === 'all') return list;
    return list.filter(client => {
      const campaigns = client.campaigns || [];
      const score = scoreClient(campaigns);
      return score.status === filterStatus;
    });
  }, [platformClients, filterStatus, accountSearch, scoreClient]);

  const inactiveAccounts = inactiveData?.[inactiveKey] || [];

  return {
    dashboardData, inactiveData, loading, inactiveAccounts,
    fetchAllData, fetchAlerts, fetchHealthData,
    expandedClient, setExpandedClient,
    activeTab, setActiveTab,
    filterStatus, setFilterStatus,
    timeframe, handleTimeframeChange,
    accountSearch, setAccountSearch,
    aiAnalysisClient, setAiAnalysisClient,
    healthData,
    alerts, alertsUnreadCount, markAlertAsRead, resolveAlert,
    aiInsights, aiInsightsLoading, aiInsightsError, fetchAiInsights,
    platformClients, filteredClients,
  };
};

export default usePerformanceData;
